import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export default function SolutionsScenes() {
  const { t } = useLanguage();
  const [activeId, setActiveId] = useState('consumer');

  const scenes = [
    {
      id: 'consumer',
      icon: 'ri-shopping-bag-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      number: '01',
      title: t('民生用品與零售包材', 'Consumer Goods & Retail Packaging', '民生用品・小売包装材'),
      desc: t(
        '將門市產生的民生廢棄塑料回收，再生後重新回到店內使用，形成清楚的循環故事。',
        'Collect consumer plastic waste from retail stores, recycle and return it to in-store use, creating a clear circular story.',
        '店舗で発生した民生廃プラスチックを回収・再生し、店内で再利用することで明確な循環ストーリーを形成します。'
      ),
      points: [
        t('rvPlastics / rvPET 回收料托盤與籃框', 'rvPlastics / rvPET recycled trays and baskets', 'rvPlastics / rvPET 再生トレー・バスケット'),
        t('品牌低碳消費場景', 'Brand low-carbon consumer scenarios', 'ブランド低炭素消費シーン'),
        t('適合連鎖零售與通路品牌', 'Suitable for chain retail and channel brands', 'チェーン小売・流通ブランドに適合'),
      ],
      image: 'https://readdy.ai/api/search-image?query=retail%20store%20sustainable%20packaging%20recycled%20plastic%20trays%20baskets%20consumer%20goods%20circular%20economy%20green%20brand%20low%20carbon%20products%20clean%20modern%20store%20environment%20professional%20photography%20green%20accent&width=640&height=380&seq=scene-consumer-v1&orientation=landscape',
      tagColor: 'text-brand-green bg-brand-green-light',
      tag: t('零售 / 通路品牌', 'Retail / Channel Brands', '小売・流通ブランド'),
    },
    {
      id: 'recycling',
      icon: 'ri-recycle-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      number: '02',
      title: t('塑膠回收與造粒廠', 'Plastic Recyclers & Compounders', 'プラスチックリサイクル・造粒業者'),
      desc: t(
        '導入 PQernel® / PQMB，提升再生料等級，讓回收與造粒端創造更高材料價值。',
        'Implement PQernel® / PQMB to upgrade recycled material grades, creating higher material value at the recycling and compounding stage.',
        'PQernel® / PQMBを導入して再生材料グレードを向上させ、リサイクル・造粒段階でより高い材料価値を創出します。'
      ),
      points: [
        t('現有設備即可導入', 'Compatible with existing equipment', '既存設備で導入可能'),
        t('提高再生料穩定性', 'Improve recycled material stability', '再生材料の安定性向上'),
        t('協助建立區域供應節點', 'Help establish regional supply nodes', '地域供給ノードの構築支援'),
      ],
      image: 'https://readdy.ai/api/search-image?query=plastic%20recycling%20granulation%20factory%20industrial%20machine%20recycled%20pellets%20production%20line%20green%20sustainable%20manufacturing%20facility%20professional%20industrial%20photography%20clean%20modern%20factory%20blue%20accent&width=640&height=380&seq=scene-recycling-v1&orientation=landscape',
      tagColor: 'text-brand-blue bg-brand-blue-light',
      tag: t('回收廠 / 造粒廠', 'Recyclers / Compounders', 'リサイクル業者・造粒業者'),
    },
    {
      id: 'manufacturing',
      icon: 'ri-settings-3-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      number: '03',
      title: t('射出與押出製造廠', 'Injection & Extrusion Manufacturers', '射出・押出メーカー'),
      desc: t(
        '協助射出、押出、成型廠商，在不大幅更換設備的情況下，提升再生料使用比例。',
        'Help injection, extrusion, and molding manufacturers increase recycled material usage without major equipment changes.',
        '射出・押出・成形メーカーが大規模な設備変更なしに再生材料使用比率を高めるよう支援します。'
      ),
      points: [
        t('降低新料依賴', 'Reduce virgin material dependency', 'バージン材料依存度の低減'),
        t('維持加工穩定性', 'Maintain processing stability', '加工安定性の維持'),
        t('配合產品目標物性調整方案', 'Adjust solutions to target product properties', '目標物性に合わせたソリューション調整'),
      ],
      image: 'https://readdy.ai/api/search-image?query=injection%20molding%20extrusion%20manufacturing%20factory%20industrial%20plastic%20processing%20machine%20modern%20production%20line%20professional%20B2B%20industrial%20photography%20clean%20facility%20green%20accent%20lighting%20high%20resolution&width=640&height=380&seq=scene-mfg-v1&orientation=landscape',
      tagColor: 'text-brand-green bg-brand-green-light',
      tag: t('射出 / 押出廠', 'Injection / Extrusion', '射出・押出メーカー'),
    },
    {
      id: 'brand',
      icon: 'ri-leaf-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      number: '04',
      title: t('品牌 ESG 與低碳產品', 'Brand ESG & Low-Carbon Products', 'ブランドESG・低炭素製品'),
      desc: t(
        '協助品牌端開發低碳商品、再生料系列與循環經濟專案，讓 ESG 從報告走向產品。',
        'Help brands develop low-carbon products, recycled material series, and circular economy projects — moving ESG from reports to products.',
        'ブランドが低炭素製品・再生材料シリーズ・サーキュラーエコノミープロジェクトを開発し、ESGをレポートから製品へと転換するよう支援します。'
      ),
      points: [
        t('低碳產品開發', 'Low-carbon product development', '低炭素製品開発'),
        t('再生料故事化', 'Recycled material storytelling', '再生材料のストーリー化'),
        t('可結合碳足跡與回收履歷', 'Combinable with carbon footprint and recycling records', 'カーボンフットプリント・回収履歴との連携可能'),
      ],
      image: 'https://readdy.ai/api/search-image?query=brand%20ESG%20sustainable%20low%20carbon%20product%20development%20recycled%20material%20packaging%20green%20label%20circular%20economy%20corporate%20sustainability%20professional%20photography%20clean%20modern%20environment%20blue%20green%20accent&width=640&height=380&seq=scene-brand-v1&orientation=landscape',
      tagColor: 'text-brand-blue bg-brand-blue-light',
      tag: t('品牌 / ESG 專案', 'Brand / ESG Projects', 'ブランド・ESGプロジェクト'),
    },
    {
      id: 'agriculture',
      icon: 'ri-plant-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      number: '05',
      title: t('農業廢棄塑膠再生', 'Agricultural Plastic Waste Recycling', '農業廃プラスチック再生'),
      desc: t(
        '將農業端產生的塑膠廢棄物重新導入再生製程，形成地方型循環經濟方案。',
        'Redirect agricultural plastic waste into recycling processes, forming local circular economy solutions.',
        '農業で発生するプラスチック廃棄物を再生プロセスに組み込み、地域型サーキュラーエコノミーソリューションを形成します。'
      ),
      points: [
        t('適合地方政府與農業單位', 'Suitable for local governments and agricultural units', '地方政府・農業機関に適合'),
        t('可結合示範場域', 'Combinable with demonstration sites', '実証サイトとの連携可能'),
        t('降低焚化與掩埋壓力', 'Reduce incineration and landfill pressure', '焼却・埋立圧力の低減'),
      ],
      image: 'https://readdy.ai/api/search-image?query=agricultural%20plastic%20waste%20recycling%20farm%20plastic%20film%20mulch%20collection%20circular%20economy%20local%20government%20green%20sustainable%20rural%20environment%20professional%20photography%20green%20accent%20eco%20friendly&width=640&height=380&seq=scene-agri-v1&orientation=landscape',
      tagColor: 'text-brand-green bg-brand-green-light',
      tag: t('農業 / 地方政府', 'Agriculture / Local Government', '農業・地方政府'),
    },
    {
      id: 'government',
      icon: 'ri-government-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      number: '06',
      title: t('政府、標案與示範場域', 'Government, Tenders & Demonstration Sites', '政府・入札・実証サイト'),
      desc: t(
        '與地方政府、產業園區、公共機構合作，透過材料技術、回收體系與數據化管理，建立可追蹤、可量化、可擴散的減碳案例。',
        'Collaborate with local governments, industrial parks, and public institutions to establish traceable, quantifiable, and replicable carbon reduction cases through material technology, recycling systems, and data management.',
        '地方政府・産業団地・公共機関と協力し、材料技術・回収システム・データ管理を通じて追跡可能・定量化可能・複製可能な脱炭素事例を構築します。'
      ),
      points: [
        t('循環經濟示範', 'Circular economy demonstration', 'サーキュラーエコノミー実証'),
        t('政府專案合作', 'Government project cooperation', '政府プロジェクト協力'),
        t('ESG 與碳效益資料化', 'ESG and carbon benefit data management', 'ESG・炭素効果のデータ化'),
      ],
      image: 'https://readdy.ai/api/search-image?query=government%20public%20sector%20circular%20economy%20demonstration%20project%20recycling%20facility%20smart%20city%20green%20technology%20ESG%20carbon%20reduction%20professional%20photography%20clean%20modern%20environment%20blue%20green%20accent%20Taiwan&width=640&height=380&seq=scene-gov-v1&orientation=landscape',
      tagColor: 'text-brand-blue bg-brand-blue-light',
      tag: t('政府 / 標案', 'Government / Tenders', '政府・入札'),
    },
  ];

  const active = scenes.find((s) => s.id === activeId) ?? scenes[0];

  return (
    <section className="section-padding bg-[#F5F8FB]">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">
            {t('應用場景', 'Application Scenarios', '応用シーン')}
          </p>
          <h2
            className="font-bold text-neutral-dark mb-4"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
          >
            {t(
              '六大應用場景，一套核心循環技術',
              'Six Application Scenarios, One Core Circular Technology',
              '6つの応用シーン、1つのコアサーキュラー技術'
            )}
          </h2>
          <p className="text-neutral-mid max-w-2xl mx-auto" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
            {t(
              '不論是民生消費品、電子電器、農業廢棄塑膠，或是工業包材與在地政府專案，聚量以單一 PQernel® 技術核心，延伸出完整材料與製程解決方案，協助客戶快速導入高比例再生料。',
              'Whether consumer goods, electronics, agricultural plastic waste, industrial packaging, or local government projects — PolyQ extends a single PQernel® technology core into comprehensive material and process solutions, helping clients quickly adopt high recycled content.',
              '民生消費財・電子機器・農業廃プラスチック・工業包装材・地方政府プロジェクトを問わず、聚量は単一のPQernel®技術コアから包括的な材料・製造ソリューションを展開し、高再生材料比率の迅速な導入を支援します。'
            )}
          </p>
        </div>

        {/* Desktop: tab + detail */}
        <div className="hidden lg:grid grid-cols-[280px_1fr] gap-6">
          <div className="flex flex-col gap-2">
            {scenes.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveId(s.id)}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 cursor-pointer ${
                  activeId === s.id
                    ? 'bg-white border border-brand-green/30 shadow-card'
                    : 'bg-white/60 border border-transparent hover:bg-white hover:border-neutral-border'
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${activeId === s.id ? s.iconBg : 'bg-neutral-border/40'}`}>
                  <i className={`${s.icon} text-lg ${activeId === s.id ? s.iconColor : 'text-neutral-mid'}`}></i>
                </div>
                <div className="min-w-0">
                  <span className={`text-[0.8125rem] font-bold tracking-widest block ${activeId === s.id ? 'text-brand-green' : 'text-neutral-mid'}`}>{s.number}</span>
                  <span className={`text-[1rem] font-semibold leading-tight block ${activeId === s.id ? 'text-neutral-dark' : 'text-neutral-mid'}`}>{s.title}</span>
                </div>
                {activeId === s.id && <i className="ri-arrow-right-s-line text-brand-green text-xl ml-auto flex-shrink-0"></i>}
              </button>
            ))}
          </div>

          <div key={active.id} className="bg-white rounded-card border border-neutral-border overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}>
            <div className="w-full h-[280px] overflow-hidden">
              <img src={active.image} alt={active.title} className="w-full h-full object-cover object-top" />
            </div>
            <div className="p-7">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${active.iconBg} flex-shrink-0`}>
                  <i className={`${active.icon} ${active.iconColor} text-2xl`}></i>
                </div>
                <div>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-[0.875rem] font-semibold mb-1 ${active.tagColor}`}>{active.tag}</span>
                  <h3 className="font-bold text-neutral-dark" style={{ fontSize: '1.375rem', lineHeight: '1.4' }}>{active.title}</h3>
                </div>
              </div>
              <p className="text-neutral-mid mb-5" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>{active.desc}</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {active.points.map((pt) => (
                  <div key={pt} className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F5F8FB] border border-neutral-border">
                    <i className="ri-check-line text-brand-green text-sm"></i>
                    <span className="text-neutral-dark text-[0.9375rem] font-medium whitespace-nowrap">{pt}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-green text-white text-[1.0625rem] font-semibold hover:bg-brand-green-dark transition-all duration-200 whitespace-nowrap cursor-pointer">
                <i className="ri-calendar-schedule-line"></i>
                {t('預約此場景諮詢', 'Schedule Consultation', 'この応用シーンの相談を予約')}
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile: card grid */}
        <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-5">
          {scenes.map((s) => (
            <div key={s.id} className="bg-white rounded-card border border-neutral-border overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}>
              <div className="w-full h-[160px] overflow-hidden">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${s.iconBg} flex-shrink-0`}>
                    <i className={`${s.icon} ${s.iconColor} text-lg`}></i>
                  </div>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[0.8125rem] font-semibold ${s.tagColor}`}>{s.tag}</span>
                </div>
                <h3 className="font-semibold text-neutral-dark mb-2" style={{ fontSize: '1.0625rem', lineHeight: '1.5' }}>{s.title}</h3>
                <p className="text-neutral-mid text-[1rem] leading-relaxed mb-3">{s.desc}</p>
                <ul className="space-y-1">
                  {s.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-1.5 text-neutral-mid text-[0.9375rem]">
                      <i className="ri-check-line text-brand-green text-sm mt-0.5 flex-shrink-0"></i>
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
